import { useContext, useState } from 'react';
import { Modal } from 'antd';

import { returnErrorFromResponse } from '../../../../shared/utility';
import { uploadVideo, refreshFetchFunctions, editVideo } from '../../../../apiFunctions/apiFunctions';

import { NotificationContext } from '../../../../context/Notification/NotificationContext';

import RenderForm from '../../../../components/Forms/RenderForm';
import { AppContext } from '../../../../context/App/AppContext';
import { useMutation } from '@tanstack/react-query';
import { VideoContext } from '../../../../context/Video/VideoContext';
import ManageVideoCategories from '../../ManageVideoCategories/ManageVideoCategories';

const EMPTY_VIDEO_CATEGORIES = { active: [], inActive: [] };

const selectedVideoConfig = (videoConfig = []) =>
  videoConfig
    .filter(({ options }) => Array.isArray(options) && options.length > 0)
    .map(({ name, options }) => ({ name, options }));

const getVideoCategoriesList = ({ isNew, selected, videoCategories }) => {
  if (!videoCategories) {
    return EMPTY_VIDEO_CATEGORIES;
  }

  if (isNew) {
    return videoCategories;
  }

  const activeCategories = videoCategories.active || [];
  const inactiveCategories = videoCategories.inActive || videoCategories.inactive || [];
  const activeCategoryNames = activeCategories.map(({ name }) => name.toLowerCase());
  const selectedCategoryNames = selectedVideoConfig(selected?.videoConfig).map(({ name }) => name.toLowerCase());
  const selectedInactiveCategories = inactiveCategories.filter(
    ({ name }) => selectedCategoryNames.includes(name.toLowerCase()) && !activeCategoryNames.includes(name.toLowerCase())
  );

  return {
    ...videoCategories,
    active: [...activeCategories, ...selectedInactiveCategories]
  };
};

function AddEditVideo({ selected = null, onCancel = () => {} }) {
  const { openNotification } = useContext(NotificationContext);
  const { trainersList } = useContext(AppContext);
  const { videoCategories, videoPasses } = useContext(VideoContext);
  const isNew = selected === null;
  const [showCategoriesModal, setShowCategoriesModal] = useState(false);

  const { mutate: onAddVideo, isLoading: onAddVideoIsLoading } = useMutation(
    (formData) =>
      isNew
        ? uploadVideo(
            formData,
            videoPasses.filter((product) => formData.products.includes(product._id))
          )
        : editVideo(selected._id, {
            ...formData,
            videoConfig: selectedVideoConfig(formData.videoConfig)
          }),
    {
      onSuccess: () => {
        openNotification(null, 'success', isNew ? 'Successfully uploaded!' : 'Successfully updated!');
        refreshFetchFunctions([['fetchVideos'], ['videoPasses']]);
        onCancel();
      },
      onError: (error) => {
        openNotification(null, 'error', returnErrorFromResponse(error));
      }
    }
  );

  const initialValues = isNew ? { products: [] } : selected;
  const videoCategoriesList = getVideoCategoriesList({ isNew, selected, videoCategories });

  return (
    <>
      <Modal
        open={showCategoriesModal}
        onCancel={() => setShowCategoriesModal(false)}
        footer={null}
        title="Manage categories"
        width={900}>
        <ManageVideoCategories />
      </Modal>
      <RenderForm
        formFields={['fileTitle', 'selectProducts', 'trainer', 'uploadFile', 'videoConfig']}
        excludeFormFields={isNew ? [] : ['uploadFile', 'selectProducts']}
        onFinish={onAddVideo}
        btnIsLoading={onAddVideoIsLoading}
        productsList={videoPasses}
        videoCategoriesList={videoCategoriesList}
        videoCategoryFallback={
          <div className="flex flex-wrap items-center gap-2 text-[0.85rem]">
            <span className="text-custom-red">
              No active categories available. Please activate an existing category or create a new one from
            </span>
            <button
              type="button"
              onClick={() => setShowCategoriesModal(true)}
              className="font-semibold text-custom-red underline">
              here
            </button>
          </div>
        }
        onCancel={onCancel}
        cancelBtn={!isNew}
        initialValues={initialValues}
        trainersList={trainersList}
      />
    </>
  );
}

export default AddEditVideo;
